import { StepShell, Repeatable, FileDrop, FileRow } from '../components/layout';
import { TextField, TextArea, SelectField, DateField, Checkbox } from '../components/inputs';

const blankEntry = () => ({
  _id: `exp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  experience_type: 'project',
  organization: '',
  role_title: '',
  start_date: '',
  end_date: '',
  is_current: false,
  description: '',
  link_url: '',
});

export default function ExperienceStep({
  data, update, notes = {}, config, documents, uploadDocument, removeDocument, uploadBusy,
}) {
  const entries = data.entries || [];

  const setEntries = (next) => update({ entries: next, skipped: false });

  const patch = (index, changes) => {
    setEntries(entries.map((entry, i) => (i === index ? { ...entry, ...changes } : entry)));
  };

  const decorated = entries.map((entry, index) => ({
    ...entry,
    _onRemove: () => setEntries(entries.filter((_, i) => i !== index)),
  }));

  const cvDocuments = documents.filter((d) => d.document_type === 'cv');

  return (
    <StepShell
      eyebrow="Step 5 of 18"
      title="Things you’ve worked on"
      lede="A university project counts. So does helping in a family business, running a society, or a bit of freelance work. This does not have to be formal employment."
      why="We are looking for evidence that you have applied yourself to something, not for a polished CV. Where you started matters far less than what you did."
      encouragement="Nothing to add yet? That is completely fine — plenty of people join us with no formal experience at all. Use Skip below."
    >
      <Repeatable
        entries={decorated}
        addLabel={entries.length ? 'Add another' : 'Add something you’ve worked on'}
        emptyLabel="Nothing here yet. Add anything you have worked on, or skip this step."
        titleFor={(entry, index) => entry.role_title || entry.organization || `Entry ${index + 1}`}
        onAdd={() => setEntries([...entries, blankEntry()])}
        renderEntry={(entry, index) => (
          <>
            <SelectField
              label="What kind of thing was this?"
              options={config.experienceTypes}
              value={entry.experience_type}
              note={notes[`entries.${index}.experience_type`]}
              onChange={(v) => patch(index, { experience_type: v })}
            />

            <div className="ob-grid2">
              <TextField
                label="Where"
                placeholder="Organisation, university, or your own project"
                value={entry.organization}
                note={notes[`entries.${index}.organization`]}
                onChange={(v) => patch(index, { organization: v })}
              />

              <TextField
                label="What you did"
                placeholder="e.g. Developer, Coordinator"
                value={entry.role_title}
                onChange={(v) => patch(index, { role_title: v })}
              />
            </div>

            <div className="ob-grid2">
              <DateField
                label="Started"
                optional
                value={entry.start_date}
                onChange={(v) => patch(index, { start_date: v })}
              />

              {!entry.is_current && (
                <DateField
                  label="Finished"
                  optional
                  value={entry.end_date}
                  note={notes[`entries.${index}.end_date`]}
                  onChange={(v) => patch(index, { end_date: v })}
                />
              )}
            </div>

            <Checkbox
              label="I am still doing this"
              checked={entry.is_current}
              onChange={(checked) => patch(index, { is_current: checked, end_date: '' })}
            />

            <TextArea
              label="What did you actually do?"
              optional
              rows={3}
              maxLength={600}
              placeholder="A couple of sentences is plenty."
              value={entry.description}
              note={notes[`entries.${index}.description`]}
              onChange={(v) => patch(index, { description: v })}
            />

            <TextField
              label="Link"
              optional
              placeholder="LinkedIn or project link we can look at"
              value={entry.link_url}
              onChange={(v) => patch(index, { link_url: v })}
            />
          </>
        )}
      />

      <div className="ob-divider">
        <div className="ob-group-label">CV — optional</div>
        {cvDocuments.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {cvDocuments.map((doc) => (
              <FileRow
                key={doc.id}
                name={doc.original_name}
                size={doc.size_bytes}
                onRemove={() => removeDocument(doc.id)}
              />
            ))}
          </div>
        ) : (
          <FileDrop
            accept=".pdf,.doc,.docx"
            title="Drop your CV here, or click to browse"
            hint="PDF or Word, up to 5 MB. Entirely optional."
            busy={uploadBusy}
            onFile={(file) => uploadDocument(file, 'cv')}
          />
        )}
      </div>
    </StepShell>
  );
}
